import React, {useState, useEffect} from 'react'
import {
    Button,
    FormControl,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
    VStack,
    useDisclosure,
    HStack,
    Text,
    Icon,
    Center,
    Divider,
    Portal,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {ChevronDownIcon} from '@chakra-ui/icons'
import {useIntl} from 'react-intl'
import {useHistory} from 'react-router-dom'
import PropTypes from 'prop-types'

const Sort = ({sortUrls, productSearchResult, ...otherProps}) => {
    const intl = useIntl()
    const history = useHistory()
    const {onOpen, onClose, isOpen} = useDisclosure()
    const {sortingOptions} = productSearchResult || {}
    const initialLabel =
        productSearchResult?.sortingOptions?.find(
            (option) => option.id === productSearchResult?.selectedSortingOption
        )?.label || ''

    const selectedSortingOptionLabel =
        sortingOptions?.find(
            (option) => option.id === productSearchResult?.selectedSortingOption
        ) ?? sortingOptions?.[0]

    const [selectedLabel, setSelectedLabel] = useState(initialLabel)

    // Use useStyleConfig to get styles
    const styles = useStyleConfig('Button')

    // Update label when productSearchResult changes (e.g., after a new sort is applied)
    useEffect(() => {
        const newLabel =
            productSearchResult?.sortingOptions?.find(
                (option) => option.id === productSearchResult?.selectedSortingOption
            )?.label || ''
        setSelectedLabel(newLabel)
    }, [productSearchResult])

    const handleSelect = (href, label) => {
        setSelectedLabel(label) // update immediately
        onClose()
        history.push(href)
    }

    return (
        <FormControl
            aria-label={intl.formatMessage({
                id: 'product_list.drawer.title.sort_by',
                defaultMessage: 'Sort By'
            })}
            data-testid="sf-product-list-sort"
            id="page_sort"
            width="auto"
            {...otherProps}
        >
            <Popover
                variant="sort"
                isOpen={isOpen}
                onOpen={onOpen}
                onClose={onClose}
                placement="bottom-end"
            >
                <PopoverTrigger data-testid="product-list-sort-popover-trigger">
                    <Button
                        variant="sortSelect"
                        rightIcon={
                            <Icon
                                as={ChevronDownIcon}
                                transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
                                transition="transform 0.2s"
                            />
                        }
                        justifyContent="space-between"
                    >
                        <HStack justify="space-between">
                            <Text whiteSpace="nowrap">Sort By</Text>
                            {(selectedSortingOptionLabel || selectedLabel) && (
                                <>
                                    <Center height="50px">
                                        <Divider
                                            orientation="vertical"
                                            w="1px"
                                            h="12px"
                                            bg="warmBlack"
                                        />
                                    </Center>
                                    <Text color="grey.700">
                                        {selectedSortingOptionLabel.label || selectedLabel}
                                    </Text>
                                </>
                            )}
                        </HStack>
                    </Button>
                </PopoverTrigger>
                <Portal>
                    <PopoverContent sx={{inset: '0px 0px auto auto'}}>
                        <PopoverBody>
                            <VStack align="stretch" spacing={0}>
                                {productSearchResult?.sortingOptions?.map((option, index) => {
                                    const label = option?.label
                                    const id = option?.id
                                    const href = sortUrls[index]
                                    const isSelected =
                                        id === productSearchResult?.selectedSortingOption

                                    return (
                                        <Button
                                            key={id}
                                            variant="sortLink"
                                            px={12}
                                            py={4}
                                            justifyContent="start"
                                            onClick={() => handleSelect(href, label)}
                                            borderBottom={
                                                sortUrls.length - 1 === index ? '0' : '1px solid'
                                            }
                                            borderColor="gray.50"
                                            sx={isSelected && styles.selectedSortLink}
                                        >
                                            {label}
                                        </Button>
                                    )
                                })}
                            </VStack>
                        </PopoverBody>
                    </PopoverContent>
                </Portal>
            </Popover>
        </FormControl>
    )
}

Sort.propTypes = {
    sortUrls: PropTypes.array.isRequired,
    productSearchResult: PropTypes.object
}

export default Sort
